import React, { useState, useEffect } from "react";
import { Grid, Divider } from "@material-ui/core";
import Pagination from "@material-ui/lab/Pagination";
import Skeleton from "@material-ui/lab/Skeleton";
import clsx from "clsx";
import ValetInventoryHistoricalSearch from "../ValetInventoryHistoricalSearch/ValetInventoryHistoricalSearch";
import ValetInventoryHistoryTable from "../ValetInventoryHistoryTable/ValetInventoryHistoryTable";
import ValetInventoryService from "../../../services/ValetInventoryService";
import useCurrentFacility from "../../../hooks/useCurrentFacility";
import { useEnqueueSnackbar } from "../../../hooks/useEnqueueSnackbar";
import useCancellationToken from "../../../hooks/useCancellationToken";
import apiClient from "../../../auth/apiClient";
import { useStyles } from "./ValetInventoryHistory.styles";
import { SORT_DIRECTION, SORT_BY, DEFAULT_SORT_DIRECTION } from "../ValetInventoryHistoryTable/constants";

const valetInventoryService = new ValetInventoryService(apiClient);

const ValetInventoryHistory = () => {
  const classes = useStyles();
  const { facilityID: entityID } = useCurrentFacility();
  const enqueueSnackbar = useEnqueueSnackbar();
  const pageSize = 10;

  const [searchResults, setSearchResults] = useState({ collection: [], totalCount: 0 });
  const [hasSearched, setHasSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchParams, setSearchParams] = useState(null);
  const [sortBy, setSortBy] = useState(SORT_BY.EntranceTime);
  const [sortDirection, setSortDirection] = useState(SORT_DIRECTION.DESCENDING);

  const executeSearch = useCancellationToken({
    func: async ({ cancelToken }) => {
      if (!searchParams) return;      
      setLoading(true);
      setError(null);
      try {
        const offset = (currentPage - 1) * pageSize;
        const response = await valetInventoryService.getValetInventoryHistoricalSearch(
          entityID,
          searchParams.fromDate,
          searchParams.toDate,
          searchParams.searchContexts,
          searchParams.guestType || null,
          searchParams.searchTerm || null,
          pageSize,
          offset,
          sortBy,
          sortDirection,
          cancelToken
        );

        setHasSearched(true);
        setSearchResults(response.data || { collection: [], totalCount: 0 });
      } catch (error) {
        console.error("Error searching valet inventory history:", error);
        enqueueSnackbar("Failed to search valet inventory history", { variant: "error", tag: "FailedToSearchValetInventoryHistory" });
        setSearchResults({ collection: [], totalCount: 0 });
        setHasSearched(true);
      } finally {
        setLoading(false);
      }
    },
  }).execute;

  const onSearch = (searchParamsData) => {
    setSearchParams(searchParamsData);
    setCurrentPage(1);
    setSortBy(SORT_BY.EntranceTime);
    setSortDirection(SORT_DIRECTION.DESCENDING);
  };

  const handleSortRequest = (columnId) => {
    const isSameColumn = sortBy === columnId;    
    let newDirection;

    if (isSameColumn) {
      newDirection = sortDirection === SORT_DIRECTION.ASCENDING 
        ? SORT_DIRECTION.DESCENDING 
        : SORT_DIRECTION.ASCENDING;
    } else {
      newDirection = DEFAULT_SORT_DIRECTION;
    }
    
    setSortDirection(newDirection);
    setSortBy(columnId);
    setCurrentPage(1);
  };

  useEffect(() => {
    if (searchParams) {
      executeSearch();
    }
  }, [entityID, currentPage, searchParams, sortBy, sortDirection]);

  return (
    <Grid container>
      <ValetInventoryHistoricalSearch onSearch={onSearch} loading={loading} />

      <Grid container className={clsx(classes.infoContainer)}>
        <Grid item xs={12}>
          {loading ? (
            <div className={clsx(classes.loadingWrapper)}>
              {[...Array(3)].map((_, index) => (
                <Skeleton key={index} className={classes.skeleton} animation="wave" />
              ))}
            </div>
          ) : (
            <>
              <Divider className={classes.resultsDivider} />
              {error && (
                <div className={clsx(["animate__animated", "animate__flipInX", "results", "error", classes.errorMessage])}>
                  <span className={clsx(["error-text"])}>{error}</span>
                </div>
              )}
              {hasSearched && !error && searchResults.totalCount === 0 && (
                <div className={clsx(["animate__animated", "animate__flipInX", "results", "not-found", classes.noResults])}>
                  <span className={clsx(["no-results-text"])}>No results found</span>
                </div>
              )}
            </>
          )}
        </Grid>
      </Grid>

      <Grid container item spacing={2} xs={12} className={clsx(classes.tableContainer)}>
        {!loading && hasSearched && !error && searchResults.totalCount > 0 && (
          <>
            <Pagination
              className={clsx(classes.pagination)}
              count={Math.ceil(searchResults.totalCount / pageSize)}
              onChange={(_, value) => setCurrentPage(value)}
              page={currentPage}
              shape="rounded"
              color="primary"
            />
            <ValetInventoryHistoryTable 
              results={searchResults.collection} 
              sortBy={sortBy}
              sortDirection={sortDirection}
              onSortRequest={handleSortRequest}
            />
          </>
        )}
      </Grid>
    </Grid>
  );
};

export default ValetInventoryHistory;
